/**
 * /editorial-standards page i18n catalog.
 *
 * Source: specs/GTM/GNPC-APPLICATION-KIT.md §3.3 (VN authoritative draft).
 * CRITICAL for Google News Publisher Center submission AND legal-upl
 * mitigation (transparency over methodology + UPL boundary).
 */

import type { Lang } from '../use-lang';

export interface SourceTableRow {
  source: string;
  type: string;
  trust: string;
}

export interface EditorialStandardsCopy {
  meta_title: string;
  meta_description: string;
  page_title: string;
  preamble: string;

  s1_heading: string;
  s1_body: string;

  s2_heading: string;
  s2_body: string;
  s2_table_source: string;
  s2_table_type: string;
  s2_table_trust: string;
  s2_rows: SourceTableRow[];
  s2_phase2: string;

  s3_heading: string;
  s3_model: string;
  s3_length: string;
  s3_tone: string;
  s3_forbidden: string;
  s3_case_convention: string;

  s4_heading: string;
  s4_methodology_intro: string;
  s4_step_embedding: string;
  s4_step_search: string;
  s4_step_rerank: string;
  s4_step_threshold: string;
  s4_only_corpus: string;

  s5_heading: string;
  s5_nli: string;
  s5_status: string;
  s5_spam: string;
  s5_bias: string;
  s5_forbidden: string;

  s6_heading: string;
  s6_feedback: string;
  s6_correction: string;
  s6_takedown: string;

  s7_heading: string;
  s7_byline: string;
  s7_automation: string;
  s7_human_review: string;
  s7_no_lawyer: string;

  s8_heading: string;
  s8_no_ads: string;
  s8_no_political: string;
  s8_no_lawyer_sponsor: string;
  s8_no_representation: string;
  s8_business_model: string;

  s9_heading: string;
  s9_upl: string;
  s9_ai: string;
  s9_pdpa: string;
  s9_ip: string;
  s9_press: string;
  s9_cyber: string;

  s10_heading: string;
  s10_feedback: string;
  s10_takedown: string;
  s10_dpo: string;
  s10_updated: string;

  living_doc_note: string;
}

export const EDITORIAL_VI: EditorialStandardsCopy = {
  meta_title: 'Tiêu chuẩn biên tập Asklaw',
  meta_description:
    'Quy trình + nguyên tắc biên tập Asklaw Hot News theo Luật AI 2026 Điều 28 + Google News Publisher Center.',
  page_title: 'Tiêu chuẩn biên tập Asklaw',
  preamble:
    'Trang này công bố nguyên tắc và quy trình biên tập của Asklaw Hot News, theo yêu cầu minh bạch của Luật AI 2026 Điều 28 và Google News Publisher Center.',

  s1_heading: '1. Sứ mệnh biên tập',
  s1_body:
    'Asklaw Hot News tổng hợp tin pháp luật Việt Nam từ các nguồn chính thống, tóm tắt trung lập 60-100 từ, và liên kết với Điều/Khoản gốc của văn bản pháp luật. Mục tiêu: giúp người dân hiểu pháp luật chính xác hơn báo truyền thống và nhanh hơn tự tra cứu.',

  s2_heading: '2. Lựa chọn nguồn tin',
  s2_body: 'Day-1 Asklaw đọc các nguồn chính thống sau:',
  s2_table_source: 'Nguồn',
  s2_table_type: 'Loại',
  s2_table_trust: 'Tier tin cậy',
  s2_rows: [
    {
      source: 'Kho VBPL Bộ Tư pháp (166,000 văn bản)',
      type: 'Cơ quan nhà nước (kho gốc)',
      trust: '1 (cao nhất)',
    },
    {
      source: 'Cổng TTĐT Chính phủ (chinhphu.vn)',
      type: 'Cơ quan nhà nước',
      trust: '2',
    },
    {
      source: 'Bộ Tư pháp news (vbpl.moj.gov.vn)',
      type: 'Cơ quan nhà nước',
      trust: '2',
    },
    {
      source: 'Báo Pháp luật Việt Nam (baophapluat.vn)',
      type: 'Báo chuyên ngành (cơ quan ngôn luận Bộ Tư pháp)',
      trust: '2',
    },
    {
      source: 'VnExpress (vnexpress.net)',
      type: 'Báo điện tử mainstream',
      trust: '3',
    },
  ],
  s2_phase2:
    'Tiêu chí thêm nguồn mới Phase 2: phải là báo chính thống có giấy phép xuất bản hợp lệ tại Việt Nam, hoặc cơ quan nhà nước cấp tỉnh trở lên.',

  s3_heading: '3. Phương pháp tóm tắt AI',
  s3_model:
    'Mô hình AI: Google Gemini Flash 3 (cập nhật khi mô hình mới phát hành).',
  s3_length: 'Độ dài: 60-100 từ tiếng Việt.',
  s3_tone: 'Giọng: Trung lập, không ý kiến, không tư vấn cá nhân.',
  s3_forbidden:
    'Cấm: tư vấn cụ thể ("anh nên..."), opinion, ước tính số liệu không có nguồn, dự đoán kết quả vụ án.',
  s3_case_convention:
    'Quy ước vụ án: dùng "theo cáo buộc" / "theo điều tra" / "theo báo X" để tôn trọng Luật Tố tụng Hình sự 2015 Điều 13 (suy đoán vô tội).',

  s4_heading: '4. Phương pháp trích dẫn',
  s4_methodology_intro:
    'Mỗi tóm tắt được liên kết với Điều/Khoản gốc của VBPL chính qua quy trình:',
  s4_step_embedding: 'Embedding BGE-m3 1024 chiều',
  s4_step_search: 'Vectorize semantic search top 5',
  s4_step_rerank: 'Cross-encoder rerank top 3-5',
  s4_step_threshold:
    'Confidence threshold ≥ 0.6 (dưới ngưỡng → không hiển thị citation, không bao giờ fake)',
  s4_only_corpus:
    'Citation chỉ tới VBPL chính của Bộ Tư pháp, KHÔNG cite báo như luật.',

  s5_heading: '5. Quy trình kiểm chứng',
  s5_nli:
    'NLI cross-check: Mode A (vụ án) — tóm tắt được kiểm tra ngữ nghĩa với citation top-1; nếu mâu thuẫn → không hiển thị.',
  s5_status:
    'Status badge: Mode B (luật mới) — luôn hiển thị trạng thái "Mới ban hành / Sắp hiệu lực / Đang hiệu lực / Hết hiệu lực".',
  s5_spam: 'Spam filter: regex blocklist + admin review queue.',
  s5_bias:
    'Bias filter: tin có thiên vị chính trị → flag bias_flag=political + lọc khỏi feed mặc định.',
  s5_forbidden:
    'Forbidden topic: nội dung tôn giáo nhạy cảm + chính trị đối lập → blocklist regex + admin review.',

  s6_heading: '6. Chính sách sửa lỗi',
  s6_feedback:
    'Phản hồi sai sót: gửi bug@asklaw.vn hoặc dùng nút "Báo lỗi" trên mỗi card.',
  s6_correction:
    'Sửa lỗi: bổ sung "Cập nhật [thời gian]" vào cuối tóm tắt + dấu hiệu visual rõ ràng.',
  s6_takedown:
    'Gỡ tin: theo yêu cầu nguồn (takedown 48h SLA) hoặc nội bộ phát hiện sai → gỡ + log đầy đủ.',

  s7_heading: '7. Đội biên tập',
  s7_byline: '"Asklaw Editorial Team" là byline tập thể.',
  s7_automation:
    'Hot News Day-1 chủ yếu AI tự động (Flash 3 zero-shot + retrieval).',
  s7_human_review:
    'Human review (founder + admin agent): tin có flag bias_flag=political / topic=chinh_tri_nhay_cam / requires_review=1.',
  s7_no_lawyer:
    'Không có nhà báo/luật sư cá nhân chịu trách nhiệm — Asklaw không phải tổ chức báo chí có giấy phép xuất bản (Asklaw công khai điều này).',

  s8_heading: '8. Xung đột lợi ích',
  s8_no_ads: 'KHÔNG có quảng cáo trong news Day-1 + Phase 1.',
  s8_no_political: 'KHÔNG có liên kết chính trị nào.',
  s8_no_lawyer_sponsor: 'KHÔNG có tài trợ từ luật sư/văn phòng luật.',
  s8_no_representation:
    'KHÔNG đại diện cho bất kỳ cá nhân/doanh nghiệp nào trong vụ việc cụ thể.',
  s8_business_model:
    'Asklaw là công cụ AI thương mại với mô hình kinh doanh: Doc Pack (Phase 2 paid), Pro subscription (Phase 3+) — KHÔNG ảnh hưởng nội dung Hot News.',

  s9_heading: '9. Tuân thủ pháp luật',
  s9_upl:
    'UPL: Luật Luật sư Điều 4 — Asklaw không hành nghề luật, không tư vấn cụ thể.',
  s9_ai:
    'Luật AI 2026 Điều 28 — minh bạch hệ thống AI, công bố mô hình + dữ liệu huấn luyện + giới hạn.',
  s9_pdpa:
    'Luật PDP 2026 + Nghị định 13/2023 — bảo vệ dữ liệu subscriber + quyền rút consent.',
  s9_ip:
    'Luật SHTT 2005 Điều 25 — trích dẫn báo chí hợp lý (≤300 ký tự + nguồn + link gốc).',
  s9_press: 'Luật Báo chí 2016 Điều 38 — quy chế dẫn tin.',
  s9_cyber: 'Luật ANM 2018 Điều 8 — không đăng nội dung cấm.',

  s10_heading: '10. Liên hệ phản hồi',
  s10_feedback: 'Phản hồi nội dung: bug@asklaw.vn',
  s10_takedown: 'Yêu cầu gỡ tin: legal@asklaw.vn (SLA 48h)',
  s10_dpo: 'Bảo vệ dữ liệu: dpo@asklaw.vn',
  s10_updated: 'Cập nhật cuối: 2026-05-05',

  living_doc_note:
    'Trang này là tài liệu sống. Phiên bản hiện tại: v1.0 (2026-05-05). Sẽ cập nhật khi quy trình biên tập thay đổi.',
};

export const EDITORIAL_EN: EditorialStandardsCopy = {
  meta_title: 'Asklaw Editorial Standards',
  meta_description:
    'Asklaw Hot News editorial principles + workflow per AI Law 2026 Article 28 and Google News Publisher Center.',
  page_title: 'Asklaw Editorial Standards',
  preamble:
    'This page publishes the editorial principles and workflow of Asklaw Hot News, per the transparency requirements of AI Law 2026 Article 28 and Google News Publisher Center.',

  s1_heading: '1. Editorial mission',
  s1_body:
    "Asklaw Hot News aggregates Vietnam legal news from official sources, summarises in 60-100 words with neutral tone, and links to the original Article/Clause in the source legislation. Goal: help citizens understand the law more accurately than traditional newspapers and faster than self-research.",

  s2_heading: '2. Source selection',
  s2_body: 'Day-1 Asklaw reads the following trusted sources:',
  s2_table_source: 'Source',
  s2_table_type: 'Type',
  s2_table_trust: 'Trust tier',
  s2_rows: [
    {
      source: 'Ministry of Justice VBPL corpus (166,000 docs)',
      type: 'State agency (origin store)',
      trust: '1 (highest)',
    },
    {
      source: 'Government Portal (chinhphu.vn)',
      type: 'State agency',
      trust: '2',
    },
    {
      source: 'Ministry of Justice news (vbpl.moj.gov.vn)',
      type: 'State agency',
      trust: '2',
    },
    {
      source: 'Vietnam Law Newspaper (baophapluat.vn)',
      type: 'Specialised press (MoJ outlet)',
      trust: '2',
    },
    {
      source: 'VnExpress (vnexpress.net)',
      type: 'Mainstream digital press',
      trust: '3',
    },
  ],
  s2_phase2:
    'Phase 2 source-add criteria: must be a licensed Vietnamese press outlet with a valid publishing licence, or a state agency at provincial level or above.',

  s3_heading: '3. AI summary methodology',
  s3_model:
    'AI model: Google Gemini Flash 3 (updated when newer models ship).',
  s3_length: 'Length: 60-100 Vietnamese words.',
  s3_tone: 'Tone: Neutral, no opinion, no personal advice.',
  s3_forbidden:
    'Forbidden: case-specific advice ("you should..."), opinion, unsourced numbers, predictions of case outcomes.',
  s3_case_convention:
    'Case convention: use "according to the indictment" / "according to the investigation" / "according to source X" to respect Criminal Procedure Code 2015 Article 13 (presumption of innocence).',

  s4_heading: '4. Citation methodology',
  s4_methodology_intro:
    'Each summary is linked to the original Article/Clause via:',
  s4_step_embedding: 'BGE-m3 1024-dim embedding',
  s4_step_search: 'Vectorize semantic search top 5',
  s4_step_rerank: 'Cross-encoder rerank top 3-5',
  s4_step_threshold:
    'Confidence threshold ≥ 0.6 (below threshold → no citation shown, never fake)',
  s4_only_corpus:
    'Citations only point to Ministry of Justice VBPL corpus — never cite a newspaper as if it were law.',

  s5_heading: '5. Fact-checking workflow',
  s5_nli:
    'NLI cross-check: Mode A (cases) — summary is semantically checked against the top-1 citation; if contradiction → not displayed.',
  s5_status:
    'Status badge: Mode B (new law) — always shows "Just published / Effective soon / In force / Repealed".',
  s5_spam: 'Spam filter: regex blocklist + admin review queue.',
  s5_bias:
    'Bias filter: politically biased stories → flag bias_flag=political + filtered out of default feed.',
  s5_forbidden:
    'Forbidden topic: sensitive religious + opposition political content → regex blocklist + admin review.',

  s6_heading: '6. Corrections policy',
  s6_feedback:
    'Report errors: email bug@asklaw.vn or use the "Report" button on each card.',
  s6_correction:
    'Corrections: append "Updated [time]" at the end of the summary plus a clear visual marker.',
  s6_takedown:
    'Removals: per source request (48-hour takedown SLA) or internal discovery → remove + full log.',

  s7_heading: '7. Editorial team',
  s7_byline: '"Asklaw Editorial Team" is a collective byline.',
  s7_automation:
    'Hot News Day-1 is primarily AI-automated (Flash 3 zero-shot + retrieval).',
  s7_human_review:
    'Human review (founder + admin agent): stories flagged bias_flag=political / topic=chinh_tri_nhay_cam / requires_review=1.',
  s7_no_lawyer:
    'No individual journalist/lawyer is named as accountable — Asklaw is not a press organisation with a publishing licence (Asklaw publishes this fact transparently).',

  s8_heading: '8. Conflict of interest',
  s8_no_ads: 'NO ads in news Day-1 + Phase 1.',
  s8_no_political: 'NO political affiliation.',
  s8_no_lawyer_sponsor: 'NO sponsorship from law firms / lawyers.',
  s8_no_representation:
    'NO representation of any individual/business in any specific case.',
  s8_business_model:
    'Asklaw is a commercial AI tool with the business model: Doc Pack (Phase 2 paid), Pro subscription (Phase 3+) — these do NOT influence Hot News content.',

  s9_heading: '9. Legal compliance',
  s9_upl:
    'UPL: Lawyer Law Article 4 — Asklaw does not practice law, does not give specific advice.',
  s9_ai:
    'AI Law 2026 Article 28 — AI system transparency: model + training data + limitations published.',
  s9_pdpa:
    'PDP Law 2026 + Decree 13/2023 — subscriber data protection + right to withdraw consent.',
  s9_ip:
    'IP Law 2005 Article 25 — fair-use journalistic citation (≤300 chars + source + original link).',
  s9_press: 'Press Law 2016 Article 38 — citation regulation.',
  s9_cyber: 'Cybersecurity Law 2018 Article 8 — no prohibited content.',

  s10_heading: '10. Editorial feedback',
  s10_feedback: 'Content feedback: bug@asklaw.vn',
  s10_takedown: 'Takedown requests: legal@asklaw.vn (48h SLA)',
  s10_dpo: 'Data protection: dpo@asklaw.vn',
  s10_updated: 'Last updated: 2026-05-05',

  living_doc_note:
    'This page is a living document. Current version: v1.0 (2026-05-05). Will be updated whenever the editorial workflow changes.',
};

export function resolveEditorial(lang: Lang): EditorialStandardsCopy {
  return lang === 'en' ? EDITORIAL_EN : EDITORIAL_VI;
}
